import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Search, X, LayoutGrid, List, Loader2 } from 'lucide-react';
import { Card, Button, Input } from '../components';
import PoolOvoContractSign from './PoolOvoContractSign';
import { OnderhandelingRijKaart, euro } from './OnderhandelingRijKaart';
import PoolDekkingsDashboard from './PoolDekkingsDashboard';
import {
  fetchOnderhandelingenOverzicht,
  herinnerOnderhandeling,
  archiveerOnderhandeling,
  startTenderVoorOnderhandeling,
  werkgeverReageertOpOnderhandeling,
  type OnderhandelingRij,
} from '../services/poolOnderhandelingApi';

const BADGE_CONFIG: Array<{ key: 'uitgenodigd' | 'tegenvoorstellen' | 'akkoord' | 'actief' | 'afgewezen'; dot: string; labelKey: string; fallback: string }> = [
  { key: 'uitgenodigd', dot: 'bg-blue-500', labelKey: 'onderhandelingen.badgeUitgenodigd', fallback: 'Uitgenodigd' },
  { key: 'tegenvoorstellen', dot: 'bg-orange-500', labelKey: 'onderhandelingen.badgeTegenvoorstellen', fallback: 'Tegenvoorstellen' },
  { key: 'akkoord', dot: 'bg-green-500', labelKey: 'onderhandelingen.badgeAkkoord', fallback: 'Akkoord (wacht op SMS)' },
  { key: 'actief', dot: 'bg-emerald-600', labelKey: 'onderhandelingen.badgeActief', fallback: 'Actief in pool' },
  { key: 'afgewezen', dot: 'bg-red-500', labelKey: 'onderhandelingen.badgeAfgewezen', fallback: 'Afgewezen' },
];

/**
 * Subtab 'Status onderhandelingen' — de onderhandelingsfase van de
 * pool-inrichting. Statistieken-balk + doorzoekbare onderhandelings-matrix
 * met per rij de exacte tariefgeschiedenis en de bijpassende actieknoppen.
 */
export const EmployerOnderhandelingen: React.FC = () => {
  const { t } = useTranslation('employer');
  const queryClient = useQueryClient();
  const [zoek, setZoek] = useState('');
  const [bezig, setBezig] = useState<string | null>(null);
  const [contractRij, setContractRij] = useState<OnderhandelingRij | null>(null);
  const [afwijsRij, setAfwijsRij] = useState<OnderhandelingRij | null>(null);
  const [afwijsReden, setAfwijsReden] = useState('');
  const [tegenbodRij, setTegenbodRij] = useState<OnderhandelingRij | null>(null);
  const [tegenbodTarief, setTegenbodTarief] = useState('');
  const [tegenbodToelichting, setTegenbodToelichting] = useState('');
  const [uitgeklapt, setUitgeklapt] = useState<string | null>(null);
  const [weergave, setWeergave] = useState<'dashboard' | 'lijst'>('dashboard');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pool-onderhandelingen-overzicht'],
    queryFn: async () => (await fetchOnderhandelingenOverzicht()).data?.data,
    refetchInterval: 30_000,
  });

  const herlaad = () => queryClient.invalidateQueries({ queryKey: ['pool-onderhandelingen-overzicht'] });

  const matrix = useMemo(() => data?.matrix ?? [], [data?.matrix]);
  const gefilterd = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    if (!q) return matrix;
    return matrix.filter(
      (r) =>
        r.professional_naam.toLowerCase().includes(q) ||
        r.discipline_label.toLowerCase().includes(q) ||
        (r.vestiging || '').toLowerCase().includes(q)
    );
  }, [matrix, zoek]);

  const uitvoeren = async (id: string, actie: () => Promise<any>, succesBericht?: string) => {
    setBezig(id);
    try {
      await actie();
      if (succesBericht) toast.success(succesBericht);
      herlaad();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'De actie is mislukt.');
    } finally {
      setBezig(null);
    }
  };

  const handleHerinner = (r: OnderhandelingRij) =>
    uitvoeren(r.id, () => herinnerOnderhandeling(r.id), 'Herinnering verstuurd');
  const handleTrekIn = (r: OnderhandelingRij) =>
    uitvoeren(r.id, () => werkgeverReageertOpOnderhandeling(r.id, 'intrekken'), 'Uitnodiging ingetrokken');
  const handleAccepteerTegenbod = (r: OnderhandelingRij) =>
    uitvoeren(r.id, () => werkgeverReageertOpOnderhandeling(r.id, 'akkoord'), 'Tegenvoorstel geaccepteerd');
  const handleArchiveer = (r: OnderhandelingRij) => uitvoeren(r.id, () => archiveerOnderhandeling(r.id), 'Rij gearchiveerd');
  const handleTender = (r: OnderhandelingRij) =>
    uitvoeren(r.id, () => startTenderVoorOnderhandeling(r.id), 'Open vacature geplaatst op de marktplaats');

  const openTegenbod = (r: OnderhandelingRij) => {
    setTegenbodRij(r);
    setTegenbodTarief(r.bod_werkgever != null ? String(r.bod_werkgever) : r.bandbreedte_min != null ? String(r.bandbreedte_min) : '');
    setTegenbodToelichting('');
  };

  const bevestigTegenbod = async () => {
    if (!tegenbodRij) return;
    const tarief = parseFloat(tegenbodTarief);
    if (!Number.isFinite(tarief) || tarief <= 0) {
      toast.error('Voer een geldig uurtarief in.');
      return;
    }
    await uitvoeren(
      tegenbodRij.id,
      () => werkgeverReageertOpOnderhandeling(tegenbodRij.id, 'tegenvoorstel', { tarief, toelichting: tegenbodToelichting || null }),
      'Tegenbod verstuurd — de professional kan accepteren of weigeren'
    );
    setTegenbodRij(null);
  };

  const bevestigAfwijzing = async () => {
    if (!afwijsRij) return;
    await uitvoeren(
      afwijsRij.id,
      () => werkgeverReageertOpOnderhandeling(afwijsRij.id, 'afwijzen', { toelichting: afwijsReden || null }),
      'Tegenvoorstel afgewezen'
    );
    setAfwijsRij(null);
    setAfwijsReden('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-12 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" />
        Onderhandelingen laden…
      </div>
    );
  }

  if (isError || !data) {
    return <p className="text-sm text-red-600">Kon de onderhandelingen niet laden.</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          {t('onderhandelingen.title', 'Status onderhandelingen')}
        </h2>
        <p className="text-sm text-gray-500">
          {t(
            'onderhandelingen.subtitle',
            'Overzicht van alle uitgenodigde professionals per vestiging en de stand van hun tariefonderhandeling.'
          )}
        </p>
      </div>

      {/* Hoofd-statusbadges */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {BADGE_CONFIG.map((b) => (
            <div key={b.key} className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm">
              <span className={`w-2.5 h-2.5 rounded-full ${b.dot}`} />
              <span className="font-medium text-gray-900">{data.badges[b.key]}</span>
              <span className="text-gray-500">{t(b.labelKey, b.fallback)}</span>
            </div>
          ))}
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
          <button
            type="button"
            onClick={() => setWeergave('dashboard')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md ${weergave === 'dashboard' ? 'bg-[#0B1F3A] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            <LayoutGrid className="w-4 h-4" /> Dekkingsdashboard
          </button>
          <button
            type="button"
            onClick={() => setWeergave('lijst')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md ${weergave === 'lijst' ? 'bg-[#0B1F3A] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            <List className="w-4 h-4" /> Volledige lijst
          </button>
        </div>
      </div>

      {weergave === 'dashboard' ? (
        <PoolDekkingsDashboard
          data={data}
          bezig={bezig}
          uitgeklapt={uitgeklapt}
          onToggleUitklap={(id) => setUitgeklapt(uitgeklapt === id ? null : id)}
          onHerinner={handleHerinner}
          onTrekIn={handleTrekIn}
          onAccepteerTegenbod={handleAccepteerTegenbod}
          onTegenbodOpenen={openTegenbod}
          onAfwijzenOpenen={(r) => setAfwijsRij(r)}
          onArchiveer={handleArchiveer}
          onTender={handleTender}
          onOpenContract={(r) => setContractRij(r)}
        />
      ) : (
        <>
          {/* Zoekbalk */}
          <div className="relative max-w-sm">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              placeholder={t('onderhandelingen.zoekPlaceholder', 'Zoek op naam, discipline of vestiging…')}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
            />
          </div>

          {/* Onderhandelings-matrix */}
          {gefilterd.length === 0 ? (
            <Card>
              <p className="text-sm text-gray-500 text-center py-8">
                {matrix.length === 0
                  ? t('onderhandelingen.leeg', 'Nog geen professionals benaderd voor uw pool.')
                  : t('onderhandelingen.geenResultaten', 'Geen resultaten voor deze zoekopdracht.')}
              </p>
            </Card>
          ) : (
            <div className="space-y-2">
              {gefilterd.map((r) => (
                <OnderhandelingRijKaart
                  key={r.id}
                  r={r}
                  isBezig={bezig === r.id}
                  uitgeklapt={uitgeklapt === r.id}
                  onToggleUitklap={() => setUitgeklapt(uitgeklapt === r.id ? null : r.id)}
                  onHerinner={() => handleHerinner(r)}
                  onTrekIn={() => handleTrekIn(r)}
                  onAccepteerTegenbod={() => handleAccepteerTegenbod(r)}
                  onTegenbodOpenen={() => openTegenbod(r)}
                  onAfwijzenOpenen={() => setAfwijsRij(r)}
                  onArchiveer={() => handleArchiveer(r)}
                  onTender={() => handleTender(r)}
                  onOpenContract={() => setContractRij(r)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal: Model-OvO tekenen */}
      {contractRij && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <div>
                <h3 className="font-semibold text-gray-900">Model-OvO — {contractRij.professional_naam}</h3>
                <p className="text-xs text-gray-500">{contractRij.discipline_label} · {euro(contractRij.akkoord_tarief)} per uur</p>
              </div>
              <button onClick={() => setContractRij(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {/* De contract-API werkt op het onderhandeling-id en genereert de Model-OvO zo nodig bij openen */}
              <PoolOvoContractSign
                contractId={contractRij.id}
                onGetekend={() => {
                  herlaad();
                  setContractRij(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal: tegenbod doen op het voorstel van de professional */}
      {tegenbodRij && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Tegenbod doen</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {tegenbodRij.professional_naam} ({tegenbodRij.discipline_label}) vraagt {euro(tegenbodRij.tegenbod_professional)} per uur.
                {tegenbodRij.bandbreedte_min != null && ` Marktconforme ondergrens: ${euro(tegenbodRij.bandbreedte_min)}.`}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <Input
                label="Uw tegenbod (€ per uur, excl. btw)"
                type="number"
                step="0.01"
                min={0}
                value={tegenbodTarief}
                onChange={(e) => setTegenbodTarief(e.target.value)}
              />
              <Input
                label="Motivatie (optioneel, zichtbaar voor de professional)"
                value={tegenbodToelichting}
                onChange={(e) => setTegenbodToelichting(e.target.value)}
                placeholder="Bijv. gezien ons budget kunnen we maximaal € 110,- bieden"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setTegenbodRij(null)}>
                  Annuleren
                </Button>
                <Button onClick={bevestigTegenbod} loading={bezig === tegenbodRij.id}>
                  Verstuur tegenbod
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: tegenbod afwijzen */}
      {afwijsRij && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Tegenvoorstel afwijzen</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {afwijsRij.professional_naam} vraagt {euro(afwijsRij.tegenbod_professional)} per uur.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <Input
                label="Toelichting (optioneel, zichtbaar voor de zzp'er)"
                value={afwijsReden}
                onChange={(e) => setAfwijsReden(e.target.value)}
                placeholder="Bijv. past niet binnen ons budget voor deze discipline"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setAfwijsRij(null); setAfwijsReden(''); }}>
                  Annuleren
                </Button>
                <Button variant="danger" onClick={bevestigAfwijzing}>
                  Wijs tegenvoorstel af
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerOnderhandelingen;
